import type { NewCustomFieldParam } from '@commenda/api';
import type {
  BuilderFieldType,
  CustomFieldDraft,
  FieldDefinition,
} from './types';

// PASSWORD intentionally omitted: the engine stores it as plaintext valueText (masked input,
// unencrypted storage) — misleading, so don't offer it until it's handled properly.
export const SIMPLE_TYPES: BuilderFieldType[] = ['TEXT', 'URL', 'EMAIL', 'DATE'];

export const TYPE_LABEL: Record<BuilderFieldType, string> = {
  TEXT: 'Text',
  URL: 'URL',
  EMAIL: 'Email',
  DATE: 'Date',
  PASSWORD: 'Password',
  FILE: 'Attach a file',
};

export const PLACEHOLDER: Record<BuilderFieldType, string> = {
  TEXT: 'Enter text',
  URL: 'https://',
  EMAIL: 'name@example.com',
  DATE: '',
  PASSWORD: '••••••••',
  FILE: '',
};

export const newDefinition = (type: BuilderFieldType): FieldDefinition => ({
  type,
  label: '',
  isRequired: true,
  saveForFuture: true,
});

// FILE values are shaped { fileId }; a missing or non-numeric fileId counts as empty. Every other
// type is empty when undefined or blank.
export const valueIsEmpty = (type: string, value: unknown): boolean => {
  if (type === 'FILE') {
    return !(
      typeof value === 'object' &&
      value !== null &&
      'fileId' in value &&
      typeof (value as { fileId?: unknown }).fileId === 'number'
    );
  }
  return value === undefined || value === '';
};

export const isFieldEmpty = (field: CustomFieldDraft): boolean =>
  valueIsEmpty(field.type, field.value);

export const customFieldsHaveMissingRequired = (
  fields: CustomFieldDraft[]
): boolean =>
  fields.some(
    (field) =>
      field.label.trim().length > 0 && field.isRequired && isFieldEmpty(field)
  );

export const customFieldDraftsToParams = (
  drafts: CustomFieldDraft[]
): NewCustomFieldParam[] =>
  drafts
    .filter((draft) => draft.label.trim().length > 0)
    .map((draft) => ({
      label: draft.label.trim(),
      type: draft.type,
      isRequired: draft.isRequired,
      saveForFuture: draft.saveForFuture,
      value: draft.type === 'FILE' && isFieldEmpty(draft) ? undefined : draft.value,
    }));
